import React, { useState } from "react";
import { Panel, Text, Pill, Icon, Skeleton } from "components/commons";
import lang from "translations";
import { PillType } from "enums";
import Timeline from "../../timeline";
import TimezoneNote from "../../timezone-note";

const lifecycleStatusPillType = (status) => {
  switch ((status || "").toUpperCase()) {
    case "SERVED":
    case "COMPLETED":
    case "PAID":
      return PillType.Green;
    case "VOIDED":
    case "CANCELLED":
      return PillType.Red;
    default:
      return PillType.Yellow;
  }
};

const ItemRow = ({ item, isLast }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className={isLast ? "" : "border-b border-gray-lightest"}>
      <div
        className="grid grid-cols-12 gap-6 px-md py-sm items-center cursor-pointer hover:bg-gray-lightest"
        onClick={() => setOpen((v) => !v)}
      >
        <div className="col-span-5 flex items-center gap-2">
          <Icon
            name={open ? "arrow-down" : "arrow-right"}
            fontSize="9px"
            className="text-gray-light"
          />
          <Text size="text-sm" className="break-all">{item.productName}</Text>
        </div>
        <Text size="text-sm" className="col-span-1 text-right">{item.quantity}</Text>
        <Text size="text-sm" className="col-span-2 text-right">{item.price}</Text>
        <div className="col-span-2">
          <Pill size="text-xs" type={lifecycleStatusPillType(item.status)}>{item.status}</Pill>
        </div>
        <Text size="text-sm" className="col-span-2 text-right">{item.totalDurationLabel}</Text>
      </div>
      {open && (
        <div className="px-md pb-md pl-xl">
          <Timeline entries={item.statusHistory} />
        </div>
      )}
    </div>
  );
};

const OrderCard = ({ order, orderNumber }) => {
  const [orderTimelineOpen, setOrderTimelineOpen] = useState(false);

  return (
    <Panel bordered mb="0">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-sm">
        <div className="flex items-center gap-2">
          <Text strong>{lang.order} {orderNumber}</Text>
          <Pill size="text-xs" type={lifecycleStatusPillType(order.status)}>{order.status}</Pill>
          {order.isPrepaid && <Pill size="text-xs" type={PillType.Blueish}>{lang.prepaid}</Pill>}
        </div>
        <Pill size="text-xs" type={PillType.Gray}>
          {lang.totalDuration}: {order.totalDurationLabel}
        </Pill>
      </div>

      <div className="mb-md">
        <div
          className="flex items-center gap-2 cursor-pointer"
          onClick={() => setOrderTimelineOpen((v) => !v)}
        >
          <Icon
            name={orderTimelineOpen ? "arrow-down" : "arrow-right"}
            fontSize="9px"
            className="text-gray-light"
          />
          <Text size="text-sm" color="text-gray">{lang.timeline}</Text>
          <Pill size="text-xs" type={PillType.Blueish}>
            {order.statusHistory.length} {lang.events}
          </Pill>
        </div>
        {orderTimelineOpen && (
          <div className="mt-sm pl-xl">
            <Timeline entries={order.statusHistory} />
          </div>
        )}
      </div>

      <div className="border border-gray-lightest rounded">
        <div className="grid grid-cols-12 gap-6 px-md py-sm bg-gray-lightest border-b border-gray-lightest">
          <Text uppercase strong size="text-xs" color="text-gray" className="col-span-5">{lang.productName}</Text>
          <Text uppercase strong size="text-xs" color="text-gray" className="col-span-1 text-right">{lang.quantity}</Text>
          <Text uppercase strong size="text-xs" color="text-gray" className="col-span-2 text-right">{lang.price}</Text>
          <Text uppercase strong size="text-xs" color="text-gray" className="col-span-2">{lang.status}</Text>
          <Text uppercase strong size="text-xs" color="text-gray" className="col-span-2 text-right">{lang.totalDuration}</Text>
        </div>
        {order.items.map((item, index) => (
          <ItemRow
            key={item.orderItemId || item.itemLocalId}
            item={item}
            isLast={index === order.items.length - 1}
          />
        ))}
      </div>
    </Panel>
  );
};

const ReservationOrders = ({ data, loading }) => {
  const orders = data?.orders || [];

  if (loading) {
    return (
      <Panel className="mt-md">
        <Skeleton />
      </Panel>
    );
  }

  if (!orders.length) {
    return (
      <Panel className="mt-md">
        <Text color="text-gray-400">{lang.noOrdersYet}</Text>
      </Panel>
    );
  }

  return (
    <div className="mt-md">
      <div className="grid grid-cols-1 gap-3">
        {orders.map((order, index) => (
          <OrderCard key={order.orderId || order.orderLocalId} order={order} orderNumber={index + 1} />
        ))}
      </div>
      <TimezoneNote />
    </div>
  );
};

export default ReservationOrders;
